import { useLocation } from "react-router-dom";
import { useEffect } from "react";

interface PageMeta {
  title: string;
  description: string;
  robots: string;
}

const routeMeta: Record<string, PageMeta> = {
  "/": {
    title: "Q-Day Clock | Quantum Readiness Assessment Tools",
    description: "Free quantum readiness assessment tools for CIOs, CTOs, and CISOs. Estimate Q-Day, calculate your organization's preparedness score, and get board-ready recommendations in 5 minutes.",
    robots: "index, follow"
  },
  "/qday-estimator": {
    title: "Q-Day Estimator | When Will Quantum Computers Break Encryption?",
    description: "Estimate when quantum computers will break current encryption. Adjust qubit scaling, algorithmic progress, and breakthrough probabilities to predict your organization's Q-Day timeline.",
    robots: "index, follow"
  },
  "/scorecard": {
    title: "Quantum Readiness Scorecard | Assess Your PQC Preparedness",
    description: "Take our 8-question quantum readiness assessment. Get your organization's preparedness score, identify cryptographic vulnerabilities, and receive personalized migration recommendations.",
    robots: "index, follow"
  },
  "/simulator": {
    title: "Q-Day Simulator | Model Quantum Threat Impact",
    description: "Simulate the impact of Q-Day on different organizations. Model team capacity, budget requirements, and explore mitigation strategies for post-quantum transformation.",
    robots: "index, follow"
  },
  "/migration-advisor": {
    title: "PQC Migration Advisor | Algorithm-Specific Guidance",
    description: "Get algorithm-specific post-quantum cryptography migration guidance. Assess risks for RSA, ECC, AES, and receive NIST-aligned recommendations for your tech stack.",
    robots: "index, follow"
  },
  "/user-guide": {
    title: "User Guide | Q-Day Clock Documentation",
    description: "Step-by-step guide to using Q-Day Clock tools. Learn how to run quantum readiness assessments, interpret results, and facilitate workshop sessions.",
    robots: "index, follow"
  },
  "/workshop-handouts": {
    title: "Workshop Handouts | Quantum Readiness Training Materials",
    description: "Printable worksheets and handouts for quantum readiness workshops. Facilitate team assessments and executive briefings with professional materials.",
    robots: "index, follow"
  },
  "/methodology": {
    title: "Methodology | Scientific Framework Behind Q-Day Predictions",
    description: "Explore the mathematical models and research behind Q-Day Clock predictions. Understand our quantum computing timeline projections and readiness scoring algorithms.",
    robots: "index, follow"
  },
  "/sources": {
    title: "Sources & Validation | Expert-Verified Research",
    description: "Review the academic papers, industry reports, and expert validations supporting Q-Day Clock's predictions. Transparent methodology backed by peer-reviewed research.",
    robots: "index, follow"
  },
  "/privacy-policy": {
    title: "Privacy Policy | Q-Day Clock",
    description: "Learn how Q-Day Clock collects, uses, and protects your data. Our commitment to privacy and data security for quantum readiness assessments.",
    robots: "noindex, follow"
  },
  "/cookie-policy": {
    title: "Cookie Policy | Q-Day Clock",
    description: "Information about cookies and tracking technologies used on Q-Day Clock. Manage your preferences and understand our data collection practices.",
    robots: "noindex, follow"
  },
  "/terms-of-use": {
    title: "Terms of Use | Q-Day Clock",
    description: "Terms and conditions for using Q-Day Clock quantum readiness assessment tools. Legal agreement governing your use of our services.",
    robots: "noindex, follow"
  },
  "/copyright": {
    title: "Copyright Notice | Q-Day Clock",
    description: "Copyright information and intellectual property notices for Q-Day Clock content, tools, and methodology.",
    robots: "noindex, follow"
  },
  "/license-compliance": {
    title: "License Compliance | Q-Day Clock",
    description: "Open source license compliance information for third-party libraries and components used in Q-Day Clock.",
    robots: "noindex, follow"
  },
};

const defaultMeta: PageMeta = {
  title: "Q-Day Clock | Quantum Readiness Assessment",
  description: "Free quantum readiness assessment tools to estimate Q-Day and prepare for post-quantum cryptography migration.",
  robots: "index, follow"
};

export const DynamicMeta = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const meta = routeMeta[currentPath] || defaultMeta;

  useEffect(() => {
    // Update document title
    document.title = meta.title;

    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", meta.description);
    }

    // Update robots meta tag
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta");
      robotsMeta.setAttribute("name", "robots");
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", meta.robots);

    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute("content", meta.title);
    }

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute("content", meta.description);
    }

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute("content", `https://q-dayclock.com${currentPath === "/" ? "" : currentPath}`);
    }

    // Update Twitter Card tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute("content", meta.title);
    }

    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute("content", meta.description);
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", `https://q-dayclock.com${currentPath === "/" ? "/" : currentPath}`);
    }
  }, [currentPath, meta]);

  return null;
};
